/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Lease dialogs: new lease (POST /re/leases), renew (POST /re/leases/{id}/renew),
 * terminate (POST /re/leases/{id}/terminate), and the client-side cheque preview.
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, MenuItem, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import ReTable from './ReTable';
import { ContactPicker, RePicker, UserPicker } from './Pickers';
import { DateField, Num } from './Widgets';
import {
  RENT_FREQUENCIES, chequesForFrequency, computePeriodicSchedule, computeRentSchedule, errorMessage, formatDate, formatMoney, humanize, isoDate,
  round2, todayIso, toNumber, useReApi, useReGet,
} from '../api';
import { defaultCurrency, isAustralia, rentFrequencyOptions } from '../../variant';
import { leaseFormFrom } from '../../v2/applications/logic';

export const defaultEndDate = (startIso) => {
  if (!startIso) return '';
  const [y, m, d] = startIso.split('-').map(Number);
  const end = new Date(y + 1, m - 1, d);
  end.setDate(end.getDate() - 1);
  return isoDate(end);
};

export function ChequePreview({ startDate, endDate, annualRent, numberOfCheques, currency }) {
  const sched = useMemo(
    () => computeRentSchedule({ startDate, endDate, annualRent, numberOfCheques }),
    [startDate, endDate, annualRent, numberOfCheques],
  );
  return (
    <>
      <ReTable
        dense
        rows={sched.lines.map((l) => ({ ...l, id: l.sequence }))}
        emptyMessage="Enter dates, rent and cheques to preview the schedule."
        columns={[
          { key: 'sequence', label: 'Cheque', width: 60, footer: 'Total' },
          { key: 'due_date', label: 'Due', render: (l) => formatDate(l.due_date) },
          {
            key: 'amount', label: 'Amount', align: 'right', render: (l) => <Num>{formatMoney(l.amount, currency)}</Num>,
            footer: <Num bold>{formatMoney(sched.total, currency)}</Num>,
          },
        ]}
      />
      {sched.months > 0 && sched.months !== 12 && (
        <MDTypography variant="caption" color="text">{`${sched.months}-month term: rent prorated from the annual figure.`}</MDTypography>
      )}
    </>
  );
}

ChequePreview.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  annualRent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  numberOfCheques: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
};
ChequePreview.defaultProps = { startDate: null, endDate: null, annualRent: null, numberOfCheques: 1, currency: null };

/** Australia: one line per rent period (computePeriodicSchedule). */
export function PeriodicPreview({ startDate, endDate, annualRent, frequency, currency }) {
  const sched = useMemo(
    () => computePeriodicSchedule({ startDate, endDate, annualRent, frequency }),
    [startDate, endDate, annualRent, frequency],
  );
  return (
    <ReTable
      dense
      rows={sched.lines.map((l) => ({ ...l, id: l.sequence }))}
      emptyMessage="Enter dates, rent and frequency to preview the schedule."
      columns={[
        { key: 'sequence', label: 'Payment', width: 70, footer: 'Total' },
        { key: 'due_date', label: 'Due', render: (l) => formatDate(l.due_date) },
        {
          key: 'amount', label: 'Amount', align: 'right', render: (l) => <Num>{formatMoney(l.amount, currency)}</Num>,
          footer: <Num bold>{formatMoney(sched.total, currency)}</Num>,
        },
      ]}
    />
  );
}

PeriodicPreview.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  annualRent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  frequency: PropTypes.string,
  currency: PropTypes.string,
};
PeriodicPreview.defaultProps = { startDate: null, endDate: null, annualRent: null, frequency: 'WEEKLY', currency: null };

/** Rent per week from a yearly figure, and back; Australian rent is quoted weekly. */
const weeklyOf = (annual) => (toNumber(annual) ? String(round2(toNumber(annual) / 52)) : '');
const annualOf = (weekly) => (toNumber(weekly) ? String(round2(toNumber(weekly) * 52)) : '');

const blankParty = (role) => ({ key: Math.random().toString(36).slice(2), contact_id: null, role });

/**
 * prefill: a rental application's conversion pre-fill (v2 Convert). It sets
 * the unit, dates, rent and the applicant as tenant; the agent checks and
 * saves as usual. Without it the dialog behaves as before.
 */
export function NewLeaseDialog({ open, onClose, onCreated, presetUnitId, prefill }) {
  const { api } = useReApi();
  const start = todayIso();
  // Australia: weekly rent, a bond and periodic payments instead of cheques.
  const au = isAustralia();
  const initial = () => ({
    unit_id: presetUnitId || null, start_date: start, end_date: defaultEndDate(start), annual_rent: '',
    rent_frequency: au ? 'WEEKLY' : 'QUARTERLY', number_of_cheques: au ? null : 4,
    security_deposit: '', agency_fee_amount: '', ejari_no: '',
    bond_amount: '', bond_authority: '',
    broker_firm_id: null, broker_agent_id: null, status: 'ACTIVE', assigned_to: null, notes: '',
  });
  const [form, setForm] = useState(initial);
  const [parties, setParties] = useState([blankParty('TENANT'), blankParty('LANDLORD')]);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const unit = useReGet(form.unit_id ? `/re/units/${form.unit_id}` : null);

  useEffect(() => {
    if (open) {
      if (prefill) {
        const allowed = au ? rentFrequencyOptions().map((o) => o.value) : RENT_FREQUENCIES;
        const { patch, tenantContactId } = leaseFormFrom(prefill, allowed);
        const base = initial();
        setForm({ ...base, ...patch, end_date: patch.end_date || defaultEndDate(patch.start_date || base.start_date) });
        setParties([{ ...blankParty('TENANT'), contact_id: tenantContactId }, blankParty('LANDLORD')]);
      } else {
        setForm(initial());
        setParties([blankParty('TENANT'), blankParty('LANDLORD')]);
      }
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  // Prefill rent, deposit and landlord from the unit.
  useEffect(() => {
    const u = unit.data;
    if (!u) return;
    setForm((f) => ({
      ...f,
      annual_rent: f.annual_rent || (u.annual_rent != null ? String(u.annual_rent) : ''),
      security_deposit: f.security_deposit || (u.annual_rent != null ? String(round2(Number(u.annual_rent) * 0.05)) : ''),
      // Typically four weeks' rent; states set their own limits.
      bond_amount: f.bond_amount || (au && u.annual_rent != null ? String(round2((Number(u.annual_rent) / 52) * 4)) : ''),
    }));
    if (u.landlord_contact_id) {
      setParties((ps) => ps.map((p) => (p.role === 'LANDLORD' && !p.contact_id ? { ...p, contact_id: u.landlord_contact_id } : p)));
    }
  }, [unit.data, au]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const submit = async () => {
    const rent = toNumber(form.annual_rent);
    const n = parseInt(form.number_of_cheques, 10);
    if (!form.unit_id) return setErr('Choose a unit.');
    if (!form.start_date || !form.end_date || form.end_date <= form.start_date) return setErr('End date must be after the start date.');
    if (!rent || rent <= 0) return setErr('Annual rent is required.');
    if (!au && (!n || n < 1 || n > 12)) return setErr('Number of cheques must be between 1 and 12.');
    if (!parties.some((p) => p.role === 'TENANT' && p.contact_id)) return setErr('Add at least one tenant.');
    if (parties.some((p) => !p.contact_id)) return setErr('Every party row needs a contact (or remove it).');
    setSaving(true);
    setErr(null);
    try {
      const res = await api.post('/re/leases', {
        unit_id: form.unit_id,
        start_date: form.start_date,
        end_date: form.end_date,
        annual_rent: rent,
        rent_frequency: form.rent_frequency,
        ...(au
          ? { bond_amount: toNumber(form.bond_amount), bond_authority: form.bond_authority || null }
          : { number_of_cheques: n, security_deposit: toNumber(form.security_deposit) }),
        agency_fee_amount: toNumber(form.agency_fee_amount),
        parties: parties.map((p) => ({ contact_id: p.contact_id, role: p.role })),
        broker_firm_id: form.broker_firm_id || null,
        broker_agent_id: form.broker_agent_id || null,
        ejari_no: form.ejari_no || null,
        status: form.status,
        assigned_to: form.assigned_to || null,
        notes: form.notes || null,
      });
      onCreated(res.data);
    } catch (e) {
      setErr(errorMessage(e, 'Could not create the lease'));
    } finally {
      setSaving(false);
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{au ? 'New lease' : 'New tenancy contract'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RePicker
                  path="/re/units"
                  label="Unit (available)"
                  value={form.unit_id}
                  onChange={set('unit_id')}
                  params={{ status: 'AVAILABLE' }}
                  getLabel={(u) => [u.unit_number, u.building_name, u.project_name, u.annual_rent ? `${formatMoney(u.annual_rent, u.currency, { decimals: 0 })}/yr` : null].filter(Boolean).join(' · ')}
                  sx={{ width: '100%' }}
                  required
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <DateField label="Start" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v, end_date: defaultEndDate(v) }))} sx={{ width: '100%' }} required />
              </Grid>
              <Grid item xs={6} md={4}>
                <DateField label="End" value={form.end_date} onChange={set('end_date')} sx={{ width: '100%' }} required />
              </Grid>
              <Grid item xs={12} md={4}>
                {au ? (
                  <TextField
                    fullWidth size="small" type="number" label={`Weekly rent (${defaultCurrency()})`}
                    value={weeklyOf(form.annual_rent)}
                    onChange={(e) => setForm((f) => ({ ...f, annual_rent: annualOf(e.target.value) }))}
                    helperText={form.annual_rent ? `${formatMoney(form.annual_rent, null, { decimals: 0 })} a year` : null}
                    required
                  />
                ) : (
                  <TextField fullWidth size="small" type="number" label={`Annual rent (${defaultCurrency()})`} value={form.annual_rent} onChange={set('annual_rent')} required />
                )}
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  select fullWidth size="small" label="Frequency" value={form.rent_frequency}
                  onChange={(e) => setForm((f) => ({ ...f, rent_frequency: e.target.value, ...(au ? {} : { number_of_cheques: chequesForFrequency(e.target.value) }) }))}
                >
                  {au
                    ? rentFrequencyOptions().map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)
                    : RENT_FREQUENCIES.map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
                </TextField>
              </Grid>
              {!au && (
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="number" label="Number of cheques" value={form.number_of_cheques} onChange={set('number_of_cheques')} inputProps={{ min: 1, max: 12 }} />
                </Grid>
              )}
              {au ? (
                <>
                  <Grid item xs={6} md={4}>
                    <TextField fullWidth size="small" type="number" label="Bond" value={form.bond_amount} onChange={set('bond_amount')} helperText="Usually four weeks' rent" />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField fullWidth size="small" label="Bond lodged with" value={form.bond_authority} onChange={set('bond_authority')} placeholder="e.g. NSW Fair Trading" />
                  </Grid>
                </>
              ) : (
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="number" label="Security deposit" value={form.security_deposit} onChange={set('security_deposit')} />
                </Grid>
              )}
              <Grid item xs={6} md={4}>
                <TextField fullWidth size="small" type="number" label="Agency fee" value={form.agency_fee_amount} onChange={set('agency_fee_amount')} />
              </Grid>
              {!au && (
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" label="Ejari no." value={form.ejari_no} onChange={set('ejari_no')} />
                </Grid>
              )}
              <Grid item xs={6} md={4}>
                <ToggleButtonGroup exclusive size="small" value={form.status} onChange={(e, v) => v && set('status')(v)}>
                  <ToggleButton value="DRAFT">Draft</ToggleButton>
                  <ToggleButton value="ACTIVE">Active</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="button" fontWeight="bold">Parties</MDTypography>
              </Grid>
              {parties.map((p) => (
                <Grid item xs={12} key={p.key}>
                  <MDBox display="flex" gap={1} alignItems="center">
                    <TextField
                      select size="small" value={p.role} sx={{ width: 150 }}
                      onChange={(e) => setParties((ps) => ps.map((x) => (x.key === p.key ? { ...x, role: e.target.value } : x)))}
                    >
                      {['TENANT', 'OCCUPANT', 'LANDLORD', 'GUARANTOR'].map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
                    </TextField>
                    <ContactPicker
                      label={humanize(p.role)}
                      value={p.contact_id}
                      onChange={(id) => setParties((ps) => ps.map((x) => (x.key === p.key ? { ...x, contact_id: id } : x)))}
                      allowCreate
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => setParties((ps) => ps.filter((x) => x.key !== p.key))}><Icon>delete_outline</Icon></IconButton>
                    </Tooltip>
                  </MDBox>
                </Grid>
              ))}
              <Grid item xs={12}>
                <MDButton size="small" variant="text" color="info" onClick={() => setParties((ps) => [...ps, blankParty('TENANT')])}>
                  <Icon>person_add</Icon>&nbsp;Add party
                </MDButton>
              </Grid>

              <Grid item xs={12} md={6}>
                <RePicker path="/re/broker-firms" label="Broker firm (optional)" value={form.broker_firm_id} onChange={(v) => setForm((f) => ({ ...f, broker_firm_id: v, broker_agent_id: null }))} sx={{ width: '100%' }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <RePicker path="/re/broker-agents" label="Broker agent" value={form.broker_agent_id} onChange={set('broker_agent_id')} params={{ firm_id: form.broker_firm_id }} disabled={!form.broker_firm_id} sx={{ width: '100%' }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <UserPicker label="Leasing agent" value={form.assigned_to} onChange={set('assigned_to')} emptyLabel="Not set" sx={{ width: '100%' }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={set('notes')} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={5}>
            {au ? (
              <>
                <MDTypography variant="button" fontWeight="bold">Rent schedule preview</MDTypography>
                <PeriodicPreview startDate={form.start_date} endDate={form.end_date} annualRent={form.annual_rent} frequency={form.rent_frequency} />
                <MDTypography variant="caption" color="text" component="p" mt={1}>
                  One payment per period from the start date; a short last period is charged by the day. The saved schedule comes from the server.
                </MDTypography>
              </>
            ) : (
              <>
                <MDTypography variant="button" fontWeight="bold">Cheque schedule preview</MDTypography>
                <ChequePreview
                  startDate={form.start_date}
                  endDate={form.end_date}
                  annualRent={form.annual_rent}
                  numberOfCheques={form.number_of_cheques}
                />
                <MDTypography variant="caption" color="text" component="p" mt={1}>
                  Equal cheques spaced evenly from the start date; the last absorbs rounding. The saved schedule comes from the server.
                </MDTypography>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create lease'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

NewLeaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  presetUnitId: PropTypes.number,
  prefill: PropTypes.object,
};
NewLeaseDialog.defaultProps = { presetUnitId: null, prefill: null };

export function RenewLeaseDialog({ lease, open, onClose, onDone }) {
  const { api } = useReApi();
  const au = isAustralia();
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && lease) {
      const [y, m, d] = String(lease.end_date).split('-').map(Number);
      const s = isoDate(new Date(y, m - 1, d + 1));
      setForm({
        start_date: s, end_date: defaultEndDate(s), annual_rent: String(lease.annual_rent ?? ''),
        number_of_cheques: lease.number_of_cheques || 1, rent_frequency: lease.rent_frequency || 'ANNUAL',
      });
      setErr(null);
    }
  }, [open, lease]);
  const submit = async () => {
    if (!toNumber(form.annual_rent) || !form.start_date || !form.end_date || form.end_date <= form.start_date) {
      setErr('Check the dates and rent.');
      return;
    }
    setSaving(true);
    try {
      const { number_of_cheques: cheques, ...rest } = form;
      const res = await api.post(`/re/leases/${lease.id}/renew`, {
        ...rest,
        annual_rent: toNumber(form.annual_rent),
        ...(au ? {} : { number_of_cheques: parseInt(cheques, 10) }),
      });
      onDone(res.data);
    } catch (e) {
      setErr(errorMessage(e, 'Could not renew the lease'));
    } finally {
      setSaving(false);
    }
  };
  const prev = toNumber(lease?.annual_rent);
  const next = toNumber(form.annual_rent);
  const change = prev && next ? round2(((next - prev) / prev) * 100) : null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{`Renew ${lease?.lease_number || 'lease'}`}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12} md={6}>
            <MDBox display="flex" flexDirection="column" gap={2}>
              <DateField label="New start" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v, end_date: defaultEndDate(v) }))} />
              <DateField label="New end" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} />
              <TextField
                size="small" type="number" label={`Annual rent (${defaultCurrency()})`} value={form.annual_rent || ''}
                onChange={(e) => setForm((f) => ({ ...f, annual_rent: e.target.value }))}
                helperText={change !== null ? `${change >= 0 ? '+' : ''}${change}% vs current ${formatMoney(prev, lease?.currency, { decimals: 0 })}${au ? '' : ' (check the RERA rental index cap)'}` : null}
              />
              <TextField
                select size="small" label="Frequency" value={form.rent_frequency || (au ? 'WEEKLY' : 'ANNUAL')}
                onChange={(e) => setForm((f) => ({ ...f, rent_frequency: e.target.value, ...(au ? {} : { number_of_cheques: chequesForFrequency(e.target.value) }) }))}
              >
                {au
                  ? rentFrequencyOptions().map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)
                  : RENT_FREQUENCIES.map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
              </TextField>
              {!au && (
                <TextField size="small" type="number" label="Number of cheques" value={form.number_of_cheques || ''} onChange={(e) => setForm((f) => ({ ...f, number_of_cheques: e.target.value }))} />
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            {au
              ? <PeriodicPreview startDate={form.start_date} endDate={form.end_date} annualRent={form.annual_rent} frequency={form.rent_frequency} currency={lease?.currency} />
              : <ChequePreview startDate={form.start_date} endDate={form.end_date} annualRent={form.annual_rent} numberOfCheques={form.number_of_cheques} currency={lease?.currency} />}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>Renew</MDButton>
      </DialogActions>
    </Dialog>
  );
}

RenewLeaseDialog.propTypes = { lease: PropTypes.object, open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };
RenewLeaseDialog.defaultProps = { lease: null };

export function TerminateLeaseDialog({ lease, open, onClose, onDone }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ effective_date: todayIso(), reason: '' });
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setForm({ effective_date: todayIso(), reason: '' }); setErr(null); } }, [open]);
  const submit = async () => {
    if (!form.reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    try {
      await api.post(`/re/leases/${lease.id}/terminate`, form);
      onDone();
    } catch (e) {
      setErr(errorMessage(e, 'Could not terminate the lease'));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Terminate lease</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>{`Future pending ${isAustralia() ? 'rent payments' : 'cheques'} are cancelled and the unit becomes available.`}</Alert>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <DateField label="Effective date" value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} />
          <TextField size="small" multiline minRows={2} label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="error" onClick={submit}>Terminate</MDButton>
      </DialogActions>
    </Dialog>
  );
}

TerminateLeaseDialog.propTypes = { lease: PropTypes.object, open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };
TerminateLeaseDialog.defaultProps = { lease: null };
